import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import {
  Menu,
  FileText,
  Target,
  Mail,
  Rocket,
  Search,
  CheckSquare,
  Building2,
  TrendingUp,
  Mic,
  Briefcase,
  Send,
  DollarSign,
  Book,
  PenTool,
  Award,
  Gift,
  Video,
  HelpCircle,
  FileText as BlogIcon,
  ChevronDown,
  Heart,
  Users,
} from "lucide-react";

interface MenuItem {
  icon: React.ElementType;
  title: string;
  description: string;
  href: string;
  badge?: string;
}

interface MenuSection {
  header: string;
  items: MenuItem[];
}

const productsMenu: MenuSection[] = [
  {
    header: "RESUME & APPLICATIONS",
    items: [
      {
        icon: FileText,
        title: "Resume Builder",
        description: "Create ATS-optimized resumes in seconds",
        href: "/resume",
      },
      {
        icon: Mail,
        title: "Cover Letter Generator",
        description: "AI writes personalized cover letters",
        href: "/letters",
      },
      {
        icon: Target,
        title: "ATS Score Checker",
        description: "Know your score before you apply",
        href: "/ats-score-checker",
        badge: "Pro",
      },
      {
        icon: Rocket,
        title: "Smart Apply",
        description: "Apply to 100+ jobs automatically",
        href: "/ai-application",
        badge: "Premium",
      },
    ],
  },
  {
    header: "JOB SEARCH & TRACKING",
    items: [
      {
        icon: TrendingUp,
        title: "Analytics Dashboard",
        description: "Track your job search performance",
        href: "/analytics",
      },
      {
        icon: CheckSquare,
        title: "Application Tracker",
        description: "Manage all your applications in one place",
        href: "/application-tracker",
      },
      {
        icon: Search,
        title: "Job Search",
        description: "Search 1M+ jobs across all platforms",
        href: "/job-search",
      },
      {
        icon: Building2,
        title: "Company Insights",
        description: "Research companies and salaries",
        href: "/job-analyzer",
        badge: "Pro",
      },
    ],
  },
  {
    header: "INTERVIEW & CAREER",
    items: [
      {
        icon: Briefcase,
        title: "LinkedIn Optimizer",
        description: "Get noticed by recruiters",
        href: "/linkedin-optimizer",
        badge: "Pro",
      },
      {
        icon: DollarSign,
        title: "Salary Negotiation",
        description: "Tools and tips to negotiate better",
        href: "/salary-negotiation",
        badge: "Pro",
      },
      {
        icon: Mic,
        title: "AI Interview Prep",
        description: "Practice with AI for real interviews",
        href: "/ai-interview-prep",
        badge: "Premium",
      },
    ],
  },
];

const resourcesMenu: MenuSection[] = [
  {
    header: "GUIDES & LEARNING",
    items: [
      {
        icon: Book,
        title: "Resume Guide",
        description: "How to write an ATS-friendly resume",
        href: "/ats-optimization",
      },
      {
        icon: Book,
        title: "Cover Letter Guide",
        description: "Write cover letters that get responses",
        href: "/ats-optimization",
      },
      {
        icon: Book,
        title: "ATS Guide",
        description: "Everything about beating ATS systems",
        href: "/ats-optimization",
      },
      {
        icon: Book,
        title: "Interview Tips",
        description: "Ace your interviews with expert advice",
        href: "/ats-optimization",
      },
    ],
  },
  {
    header: "TEMPLATES & EXAMPLES",
    items: [
      {
        icon: PenTool,
        title: "Resume Templates",
        description: "Professional, ATS-optimized templates",
        href: "/samples",
      },
      {
        icon: PenTool,
        title: "Cover Letter Templates",
        description: "Free templates to get started",
        href: "/samples",
      },
      {
        icon: Award,
        title: "Resume Examples",
        description: "See winning resumes by industry",
        href: "/samples",
      },
      {
        icon: Mail,
        title: "Email Templates",
        description: "Follow-up and networking templates",
        href: "/samples",
      },
    ],
  },
  {
    header: "TOOLS & SUPPORT",
    items: [
      {
        icon: Gift,
        title: "Free Tools",
        description: "Free ATS checker and templates",
        href: "/ats-score-checker",
      },
      {
        icon: Video,
        title: "Video Tutorials",
        description: "Learn how to use LoopBaan",
        href: "/samples",
      },
      {
        icon: HelpCircle,
        title: "Help Center",
        description: "Get answers to your questions",
        href: "/contact",
      },
      {
        icon: BlogIcon,
        title: "Blog",
        description: "Latest career tips and advice",
        href: "/ats-optimization",
      },
    ],
  },
];

const organizationsMenu: MenuSection[] = [
  {
    header: "EDUCATION",
    items: [
      {
        icon: Building2,
        title: "Universities & Career Centers",
        description: "Empower students with enterprise career tools",
        href: "/organizations/universities",
      },
    ],
  },
  {
    header: "CORPORATE & GOVERNMENT",
    items: [
      {
        icon: Briefcase,
        title: "Corporate Outplacement",
        description: "Support transitioning employees with dignity",
        href: "/organizations/corporate-outplacement",
      },
      {
        icon: Building2,
        title: "Government Agencies",
        description: "Modern workforce solutions for citizens",
        href: "/organizations/government",
      },
    ],
  },
  {
    header: "COMMUNITY & TRAINING",
    items: [
      {
        icon: Heart,
        title: "Non-Profit Organizations",
        description: "Special pricing for social impact",
        href: "/organizations/non-profits",
      },
      {
        icon: Users,
        title: "Workforce Development",
        description: "Accelerate training program outcomes",
        href: "/organizations/workforce-development",
      },
    ],
  },
];

export const MegaMenuNav = () => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="border-b bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] sticky top-0 z-50 backdrop-blur-sm shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-90 transition-opacity">
            <Logo className="h-12 w-12" />
            <span className="text-xl font-bold">LoopBaan.ai</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Products Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setActiveDropdown("products")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="nav-item px-4 py-2 text-white font-medium hover:text-[#5eead4] transition-colors flex items-center gap-1 relative">
                Products
                <ChevronDown className="h-4 w-4 opacity-70" />
                <span className={`absolute bottom-0 left-4 right-4 h-0.5 bg-[#14b8a6] rounded-full transition-transform origin-left ${activeDropdown === "products" ? "scale-x-100" : "scale-x-0"}`} />
              </button>

              {/* Products Mega Menu */}
              {activeDropdown === "products" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[1200px]">
                  <div className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] border border-[#14b8a6]/20 border-t-0 rounded-b-xl shadow-[0_10px_40px_rgba(0,0,0,0.3)] p-8 animate-fade-in">
                    <div className="grid grid-cols-3 gap-12">
                      {productsMenu.map((section, idx) => (
                        <div key={idx}>
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
                            {section.header}
                          </h3>
                          <div className="space-y-1">
                            {section.items.map((item, itemIdx) => (
                              <Link
                                key={itemIdx}
                                to={item.href}
                                className="flex items-start gap-3 p-3 rounded-lg hover:bg-[#1e293b]/50 border-l-2 border-transparent hover:border-[#14b8a6] transition-all duration-200 hover:translate-x-0.5 group/item"
                              >
                                <item.icon className="h-5 w-5 text-[#14b8a6] flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-white">
                                      {item.title}
                                    </span>
                                    {item.badge && (
                                      <Badge className="bg-[#14b8a6] text-white text-[10px] px-2 py-0 h-5">
                                        {item.badge}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-400 leading-snug mt-0.5">
                                    {item.description}
                                  </p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Resources Dropdown - Hidden for now */}
            {/* <div
              className="relative group"
              onMouseEnter={() => setActiveDropdown("resources")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="nav-item px-4 py-2 text-white font-medium hover:text-[#5eead4] transition-colors flex items-center gap-1 relative">
                Resources
                <ChevronDown className="h-4 w-4 opacity-70" />
                <span className={`absolute bottom-0 left-4 right-4 h-0.5 bg-[#14b8a6] rounded-full transition-transform origin-left ${activeDropdown === "resources" ? "scale-x-100" : "scale-x-0"}`} />
              </button>

              {activeDropdown === "resources" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[1200px]">
                  <div className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] border border-[#14b8a6]/20 border-t-0 rounded-b-xl shadow-[0_10px_40px_rgba(0,0,0,0.3)] p-8 animate-fade-in">
                    <div className="grid grid-cols-3 gap-12">
                      {resourcesMenu.map((section, idx) => (
                        <div key={idx}>
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
                            {section.header}
                          </h3>
                          <div className="space-y-1">
                            {section.items.map((item, itemIdx) => (
                              <Link
                                key={itemIdx}
                                to={item.href}
                                className="flex items-start gap-3 p-3 rounded-lg hover:bg-[#1e293b]/50 border-l-2 border-transparent hover:border-[#14b8a6] transition-all duration-200 hover:translate-x-0.5 group/item"
                              >
                                <item.icon className="h-5 w-5 text-[#14b8a6] flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" />
                                <div className="flex-1">
                                  <span className="text-sm font-semibold text-white block">
                                    {item.title}
                                  </span>
                                  <p className="text-xs text-gray-400 leading-snug mt-0.5">
                                    {item.description}
                                  </p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 pt-5 border-t border-white/10 text-center">
                      <Link
                        to="/ats-optimization"
                        className="text-[#14b8a6] font-semibold hover:text-[#5eead4] hover:underline transition-colors"
                      >
                        Browse All Resources →
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div> */}

            {/* For Organizations Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setActiveDropdown("organizations")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="nav-item px-4 py-2 text-white font-medium hover:text-[#5eead4] transition-colors flex items-center gap-1 relative">
                Enterprise
                <ChevronDown className="h-4 w-4 opacity-70" />
                <span className={`absolute bottom-0 left-4 right-4 h-0.5 bg-[#14b8a6] rounded-full transition-transform origin-left ${activeDropdown === "organizations" ? "scale-x-100" : "scale-x-0"}`} />
              </button>

              {/* Organizations Mega Menu */}
              {activeDropdown === "organizations" && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[900px]">
                  <div className="bg-gradient-to-b from-[#1e293b] to-[#0f172a] border border-[#14b8a6]/20 border-t-0 rounded-b-xl shadow-[0_10px_40px_rgba(0,0,0,0.3)] p-8 animate-fade-in">
                    <div className="grid grid-cols-3 gap-12">
                      {organizationsMenu.map((section, idx) => (
                        <div key={idx}>
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
                            {section.header}
                          </h3>
                          <div className="space-y-1">
                            {section.items.map((item, itemIdx) => (
                              <Link
                                key={itemIdx}
                                to={item.href}
                                className="flex items-start gap-3 p-3 rounded-lg hover:bg-[#1e293b]/50 border-l-2 border-transparent hover:border-[#14b8a6] transition-all duration-200 hover:translate-x-0.5 group/item"
                              >
                                <item.icon className="h-5 w-5 text-[#14b8a6] flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" />
                                <div className="flex-1">
                                  <span className="text-sm font-semibold text-white block">
                                    {item.title}
                                  </span>
                                  <p className="text-xs text-gray-400 leading-snug mt-0.5">
                                    {item.description}
                                  </p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Direct Links */}
            <Link to="/pricing">
              <Button variant="ghost" className="text-white hover:text-[#5eead4] hover:bg-white/10">
                Pricing
              </Button>
            </Link>

            <Link to="/login">
              <Button variant="outline" className="text-white border-white/30 hover:bg-white/10 hover:border-white/50">
                Log In
              </Button>
            </Link>

            <Link to="/signup">
              <Button className="bg-[#14b8a6] hover:bg-[#0d9488] text-white shadow-lg">
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Navigation */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] bg-gradient-to-b from-[#1e293b] to-[#0f172a] border-l border-[#14b8a6]/20 overflow-y-auto">
              <div className="flex flex-col gap-2 mt-8">
                {/* Products Accordion */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-left text-white font-medium hover:bg-white/5 rounded-lg transition-colors">
                    Products
                    <ChevronDown className="h-4 w-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-1 space-y-1 pl-4">
                    {productsMenu.flatMap((section) =>
                      section.items.map((item, idx) => (
                        <Link
                          key={idx}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                          <item.icon className="h-4 w-4 text-[#14b8a6]" />
                          <span>{item.title}</span>
                          {item.badge && (
                            <Badge className="bg-[#14b8a6] text-white text-[9px] px-1.5 py-0 h-4 ml-auto">
                              {item.badge}
                            </Badge>
                          )}
                        </Link>
                      ))
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Resources Accordion - Hidden for now */}
                {/* <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-left text-white font-medium hover:bg-white/5 rounded-lg transition-colors">
                    Resources
                    <ChevronDown className="h-4 w-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-1 space-y-1 pl-4">
                    {resourcesMenu.flatMap((section) =>
                      section.items.map((item, idx) => (
                        <Link
                          key={idx}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                          <item.icon className="h-4 w-4 text-[#14b8a6]" />
                          <span>{item.title}</span>
                        </Link>
                      ))
                    )}
                  </CollapsibleContent>
                </Collapsible> */}

                {/* Organizations Accordion */}
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-left text-white font-medium hover:bg-white/5 rounded-lg transition-colors">
                    Enterprise
                    <ChevronDown className="h-4 w-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-1 space-y-1 pl-4">
                    {organizationsMenu.flatMap((section) =>
                      section.items.map((item, idx) => (
                        <Link
                          key={idx}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                        >
                          <item.icon className="h-4 w-4 text-[#14b8a6]" />
                          <span>{item.title}</span>
                        </Link>
                      ))
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Direct Links */}
                <Link to="/pricing" onClick={() => setMobileOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-white hover:bg-white/5">
                    Pricing
                  </Button>
                </Link>

                <Link to="/login" onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" className="w-full text-white border-white/30 hover:bg-white/10">
                    Log In
                  </Button>
                </Link>

                <Link to="/signup" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full bg-[#14b8a6] hover:bg-[#0d9488] text-white">
                    Get Started
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
